import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  VaButton,
  VaRadio,
  VaRadioOption,
  VaTextInput,
} from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import {
  radioSchema,
  radioUI,
  textUI,
} from 'platform/forms-system/src/js/web-component-patterns';
import { PARENT, YES } from './helpers';

const PAGE_TITLE_CLASS =
  'vads-u-color--black vads-u-margin-top--0 mobile-lg:vads-u-font-size--h2 vads-u-font-size--h3';

const isBlank = value =>
  value === undefined || value === null || String(value).trim() === '';

const getReasonData = formData => formData?.[PARENT] || {};

const getInputName = fieldName => `root_${PARENT}_${fieldName}`;

const getEventValue = event =>
  event?.detail?.value ?? event?.target?.value ?? '';

const getReasonErrors = (pageConfig, formData) => {
  const reasonData = getReasonData(formData);
  const errors = {};

  if (isBlank(reasonData[pageConfig.trigger.name])) {
    errors[pageConfig.trigger.name] = pageConfig.trigger.error;
    return errors;
  }

  if (reasonData[pageConfig.trigger.name] !== YES) {
    return errors;
  }

  (pageConfig.conditionalFields || []).forEach(fieldConfig => {
    if (isBlank(reasonData[fieldConfig.name])) {
      errors[fieldConfig.name] = fieldConfig.error;
    }
  });

  return errors;
};

const buildSchema = pageConfig => {
  const parentProperties = {
    [pageConfig.trigger.name]: radioSchema(pageConfig.trigger.options),
  };

  (pageConfig.conditionalFields || []).forEach(fieldConfig => {
    parentProperties[fieldConfig.name] =
      fieldConfig.type === 'radio'
        ? radioSchema(fieldConfig.options)
        : { type: 'string', maxLength: fieldConfig.maxLength };
  });

  return {
    type: 'object',
    properties: {
      [PARENT]: {
        type: 'object',
        properties: parentProperties,
        required: [pageConfig.trigger.name],
      },
    },
  };
};

const hideUnlessTriggerIsYes = triggerFieldName => ({
  hideIf: formData => getReasonData(formData)[triggerFieldName] !== YES,
  hideEmptyValueInReview: true,
});

const buildRadioUi = fieldConfig => {
  const ui = radioUI({
    title: fieldConfig.title,
    labels: fieldConfig.labels,
    hint: fieldConfig.hint,
  });

  if (!fieldConfig.tile && !fieldConfig.descriptions) {
    return ui;
  }

  return {
    ...ui,
    'ui:options': {
      ...(ui['ui:options'] || {}),
      descriptions: fieldConfig.descriptions,
      tile: fieldConfig.tile,
    },
  };
};

const buildUiSchema = pageConfig => {
  const parentUiSchema = {
    [pageConfig.trigger.name]: buildRadioUi(pageConfig.trigger),
  };

  (pageConfig.conditionalFields || []).forEach(fieldConfig => {
    const fieldUi =
      fieldConfig.type === 'radio'
        ? buildRadioUi(fieldConfig)
        : textUI({
            title: fieldConfig.title,
          });

    parentUiSchema[fieldConfig.name] = {
      ...fieldUi,
      'ui:options': {
        ...(fieldUi['ui:options'] || {}),
        ...hideUnlessTriggerIsYes(pageConfig.trigger.name),
      },
    };
  });

  return { [PARENT]: parentUiSchema };
};

const FOCUSABLE_SELECTOR = [
  'input:not([disabled])',
  'button:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const getFocusableChild = element =>
  element?.shadowRoot?.querySelector(FOCUSABLE_SELECTOR) ||
  element?.querySelector?.(FOCUSABLE_SELECTOR);

const focusElement = element => {
  if (!element) return;

  element.scrollIntoView({ block: 'center' });

  const focusTarget = getFocusableChild(element) || element;

  if (typeof focusTarget.setFocus === 'function') {
    focusTarget.setFocus();
    return;
  }

  if (
    focusTarget === element &&
    typeof focusTarget.hasAttribute === 'function' &&
    !focusTarget.hasAttribute('tabindex')
  ) {
    focusTarget.setAttribute('tabindex', '-1');
  }

  if (typeof focusTarget.focus === 'function') {
    focusTarget.focus({ preventScroll: true });
  }
};

const focusFirstErroredField = fieldName => {
  const inputName = getInputName(fieldName);

  const radioGroup = document.querySelector(`va-radio[name="${inputName}"]`);

  if (radioGroup) {
    const firstRadioOption = radioGroup.querySelector('va-radio-option');

    if (firstRadioOption) {
      focusElement(firstRadioOption);
      return;
    }

    focusElement(radioGroup);
    return;
  }

  const textInput = document.querySelector(
    `va-text-input[name="${inputName}"]`,
  );

  if (textInput) {
    focusElement(textInput);
  }
};

const useFocusOnFirstError = (submitAttemptCount, firstErrorFieldName) => {
  useEffect(() => {
    if (!submitAttemptCount || !firstErrorFieldName) {
      return undefined;
    }

    const requestFrame = window.requestAnimationFrame || window.setTimeout;
    const cancelFrame = window.cancelAnimationFrame || window.clearTimeout;

    const animationFrameId = requestFrame(() => {
      focusFirstErroredField(firstErrorFieldName);
    }, 0);

    return () => cancelFrame(animationFrameId);
  }, [submitAttemptCount, firstErrorFieldName]);
};

const RadioField = ({ fieldConfig, value, error, onChange }) => (
  <VaRadio
    error={error || null}
    hint={fieldConfig.hint}
    label={fieldConfig.title}
    name={getInputName(fieldConfig.name)}
    onVaValueChange={onChange}
    required
    uswds
    value={value}
  >
    {fieldConfig.options.map(optionValue => (
      <VaRadioOption
        key={optionValue}
        checked={value === optionValue}
        description={fieldConfig.descriptions?.[optionValue]}
        label={fieldConfig.labels[optionValue]}
        name={getInputName(fieldConfig.name)}
        tile={fieldConfig.tile}
        value={optionValue}
      />
    ))}
  </VaRadio>
);

RadioField.propTypes = {
  fieldConfig: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  value: PropTypes.string,
};

const ConditionalField = ({ fieldConfig, value, error, onChange }) => {
  if (fieldConfig.type === 'radio') {
    return (
      <RadioField
        error={error}
        fieldConfig={fieldConfig}
        onChange={onChange}
        value={value}
      />
    );
  }

  return (
    <VaTextInput
      error={error || null}
      label={fieldConfig.title}
      maxlength={fieldConfig.maxLength}
      name={getInputName(fieldConfig.name)}
      onInput={onChange}
      required
      uswds
      value={value || ''}
    />
  );
};

ConditionalField.propTypes = {
  fieldConfig: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
  value: PropTypes.string,
};

function ReasonPageBody({ pageConfig, ...props }) {
  const {
    contentAfterButtons,
    contentBeforeButtons,
    data,
    goForward,
    onChange,
    onReviewPage,
    setFormData,
    updatePage,
  } = props;

  const { trigger, description, extraContent, title } = pageConfig;
  const conditionalFields = pageConfig.conditionalFields || [];
  const conditionalFieldNames = conditionalFields.map(
    fieldConfig => fieldConfig.name,
  );

  const formData = data || {};
  const reasonData = getReasonData(formData);
  const updateFormData = setFormData || onChange;

  const [triggerSubmitted, setTriggerSubmitted] = useState(false);
  const [detailsSubmitted, setDetailsSubmitted] = useState(false);
  const [submitAttemptCount, setSubmitAttemptCount] = useState(0);

  const titleRef = useRef(null);

  const errors = getReasonErrors(pageConfig, formData);
  const triggerError = triggerSubmitted ? errors[trigger.name] : null;
  const shouldShowDetails = reasonData[trigger.name] === YES;

  const firstErrorFieldName = triggerError
    ? trigger.name
    : conditionalFieldNames.find(
        fieldName => detailsSubmitted && errors[fieldName],
      );

  useFocusOnFirstError(submitAttemptCount, firstErrorFieldName);

  useEffect(() => {
    titleRef.current?.focus?.();
  }, []);

  const updateReasonData = updates => {
    updateFormData({
      ...formData,
      [PARENT]: {
        ...reasonData,
        ...updates,
      },
    });
  };

  const handleTriggerChange = event => {
    const selectedValue = getEventValue(event);

    setTriggerSubmitted(false);
    setDetailsSubmitted(false);

    const updates = {
      [trigger.name]: selectedValue,
    };

    if (selectedValue !== YES) {
      conditionalFieldNames.forEach(fieldName => {
        updates[fieldName] = undefined;
      });
    }

    updateReasonData(updates);
  };

  const handleConditionalFieldChange = fieldName => event => {
    updateReasonData({
      [fieldName]: getEventValue(event),
    });
  };

  const handleContinue = () => {
    const currentErrors = getReasonErrors(pageConfig, formData);
    const hasTriggerError = !!currentErrors[trigger.name];
    const hasConditionalFieldError = conditionalFieldNames.some(
      fieldName => currentErrors[fieldName],
    );

    if (hasTriggerError) {
      setTriggerSubmitted(true);
      setDetailsSubmitted(false);
      setSubmitAttemptCount(currentCount => currentCount + 1);
      return;
    }

    if (hasConditionalFieldError) {
      setTriggerSubmitted(false);
      setDetailsSubmitted(true);
      setSubmitAttemptCount(currentCount => currentCount + 1);
      return;
    }

    if (onReviewPage && updatePage) {
      updatePage();
      return;
    }

    goForward(formData);
  };

  return (
    <>
      <h1 className={PAGE_TITLE_CLASS} ref={titleRef} tabIndex="-1">
        {title}
      </h1>

      {description && <p>{description}</p>}

      {extraContent}

      <RadioField
        error={triggerError}
        fieldConfig={trigger}
        onChange={handleTriggerChange}
        value={reasonData[trigger.name]}
      />

      {shouldShowDetails &&
        conditionalFields.map(fieldConfig => (
          <ConditionalField
            key={fieldConfig.name}
            error={detailsSubmitted ? errors[fieldConfig.name] : null}
            fieldConfig={fieldConfig}
            onChange={handleConditionalFieldChange(fieldConfig.name)}
            value={reasonData[fieldConfig.name]}
          />
        ))}

      {contentBeforeButtons}

      <div className="row form-progress-buttons schemaform-buttons vads-u-margin-y--2">
        <div className="medium-8 columns">
          <VaButton
            continue={!onReviewPage}
            fullWidth
            onClick={handleContinue}
            submit="prevent"
            text={onReviewPage ? 'Update page' : undefined}
            uswds
          />
        </div>
      </div>

      {contentAfterButtons}
    </>
  );
}

ReasonPageBody.propTypes = {
  pageConfig: PropTypes.object.isRequired,
  contentAfterButtons: PropTypes.node,
  contentBeforeButtons: PropTypes.node,
  data: PropTypes.object,
  goForward: PropTypes.func,
  setFormData: PropTypes.func,
  updatePage: PropTypes.func,
  onChange: PropTypes.func,
  onReviewPage: PropTypes.bool,
};

const buildReviewRows = (pageConfig, data) => {
  const { trigger, title } = pageConfig;
  const reasonData = getReasonData(data);

  const rows = [
    {
      label: trigger.title?.trim() || title,
      value:
        trigger.labels[reasonData[trigger.name]] ?? reasonData[trigger.name],
    },
  ];

  if (reasonData[trigger.name] === YES) {
    (pageConfig.conditionalFields || []).forEach(fieldConfig => {
      const fieldValue = reasonData[fieldConfig.name];

      rows.push({
        label: fieldConfig.title,
        value:
          fieldConfig.type === 'radio'
            ? fieldConfig.labels[fieldValue] ?? fieldValue
            : fieldValue,
      });
    });
  }

  return rows;
};

function ReasonReviewBody({ pageConfig, data, editPage }) {
  const { title } = pageConfig;
  const rows = buildReviewRows(pageConfig, data);

  return (
    <div className="form-review-panel-page">
      <div className="form-review-panel-page-header-row">
        <h3 className="form-review-panel-page-header vads-u-font-size--h5 vads-u-margin--0">
          {title}
        </h3>

        <VaButton
          label={`Edit ${title}`}
          onClick={editPage}
          secondary
          text="Edit"
          uswds
        />
      </div>

      <dl className="review">
        {rows.map(row => (
          <div className="review-row" key={row.label}>
            <dt>{row.label}</dt>
            <dd>{row.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

ReasonReviewBody.propTypes = {
  pageConfig: PropTypes.object.isRequired,
  data: PropTypes.object,
  editPage: PropTypes.func,
};

export const __testables = {
  isBlank,
  getReasonData,
  getInputName,
  getEventValue,
  getReasonErrors,
  buildSchema,
  hideUnlessTriggerIsYes,
  buildRadioUi,
  buildUiSchema,
  buildReviewRows,
};

export const buildReasonPage = pageConfig => {
  const CustomPage = pageProps => (
    <ReasonPageBody pageConfig={pageConfig} {...pageProps} />
  );

  const CustomPageReview = pageProps => (
    <ReasonReviewBody pageConfig={pageConfig} {...pageProps} />
  );

  return {
    path: pageConfig.path,
    title: pageConfig.title,
    ...(pageConfig.depends ? { depends: pageConfig.depends } : {}),
    CustomPage,
    CustomPageReview,
    uiSchema: buildUiSchema(pageConfig),
    schema: buildSchema(pageConfig),
  };
};
